import React, { useState } from 'react';
import { Mail, MapPin, Send } from 'lucide-react';
import PublicNavbar from '../../components/public/navbar/PublicNavbar';
import PublicFooter from '../../components/public/footer/PublicFooter';
import SEO from '../../components/shared/SEO';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setSubmitting(true);
  setError('');

  const apiUrl = process.env.NODE_ENV === 'production'
    ? '/contact.php'
    : 'http://localhost:8888/contact.php';

  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    const result = await response.json();

    if (result.success) {
      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } else {
      setError(result.error || 'Failed to send message. Please try again.');
    }
  } catch (err) {
    console.error('Error sending email:', err);
    setError('Failed to send message. Please email us directly at info@izzishowin.com');
  } finally {
    setSubmitting(false);
  }
};

  return (
    <div className="public-contact-page">
      <SEO
        title="Contact Us"
        description="Get in touch with IZZISHOWIN for filming locations, equipment rentals, and production services in Chicago. We're here to help with your next project."
        keywords="contact IZZISHOWIN, Chicago film production contact, booking inquiry, production services"
        url="/contact"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Contact', url: '/contact' }
        ]}
        schema={{
          '@type': 'ContactPage',
          'name': 'Contact IZZISHOWIN',
          'description': 'Contact page for IZZISHOWIN film production services in Chicago',
          'mainEntity': {
            '@type': 'LocalBusiness',
            'name': 'IZZISHOWIN',
            'email': 'info@izzishowin.com',
            'address': {
              '@type': 'PostalAddress',
              'addressLocality': 'Chicago',
              'addressRegion': 'IL',
              'addressCountry': 'US'
            }
          }
        }}
      />
      <PublicNavbar />

      <div className="page-header">
        <div className="container">
          <h1 className="page-title">Contact Us</h1>
          <p className="page-subtitle">
            Get in touch with our team
          </p>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          <div className="contact-layout">
            {/* Contact Info */}
            <div className="contact-info">
              <h2 className="info-title">Get in Touch</h2>
              <p className="info-description">
                Have questions about our services? We're here to help. Reach out and we'll get back to you as soon as possible.
              </p>

              <div className="contact-details">
                <div className="contact-detail">
                  <div className="detail-icon">
                    <Mail size={24} />
                  </div>
                  <div className="detail-content">
                    <h4>Email</h4>
                    <a href="mailto:info@izzishowin.com">info@izzishowin.com</a>
                  </div>
                </div>

                <div className="contact-detail">
                  <div className="detail-icon">
                    <MapPin size={24} />
                  </div>
                  <div className="detail-content">
                    <h4>Location</h4>
                    <p>Chicago, IL</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-container">
              {submitted ? (
                <div className="form-success">
                  <div className="success-icon">✓</div>
                  <h3>Message Sent!</h3>
                  <p>Thank you for contacting us. We'll get back to you soon.</p>
                  <button 
                    onClick={() => setSubmitted(false)}
                    className="btn btn-primary"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  <h3 className="form-title">Send us a Message</h3>

                  {error && (
                    <div className="form-error">
                      <p>{error}</p>
                    </div>
                  )}

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="Your name"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="(555) 123-4567"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Subject *</label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="How can we help?"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      className="form-textarea"
                      rows="6"
                      placeholder="Tell us about your project..."
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary btn-lg"
                    disabled={submitting}
                  >
                    {submitting ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send size={18} />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
};

export default Contact;